import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AuthStateFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { DepotInfrastructure } from '../infrastructure/depot.infrastructure';
import { DepotActions, DepotApiActions } from './depot.actions';

@Injectable()
export class DepotEffects {
  readonly #actions$ = inject(Actions);
  readonly #depotInfrastructure = inject(DepotInfrastructure);
  readonly #authStateFacade = inject(AuthStateFacade);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public loadUserInterests$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(DepotActions.loadUserInterests),
      filter(() => !!this.#authStateFacade.customerId),
      switchMap(() =>
        this.#depotInfrastructure.getUserInterests().pipe(
          map((userInterests) => DepotApiActions.loadUserInterestsSuccess({ userInterests })),
          catchError((error: HttpErrorResponse) => of(DepotApiActions.loadUserInterestsFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(DepotApiActions.loadUserInterestsFailure({ error }))),
    );
  });

  public dismissSavingsPlanTeaser$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(DepotActions.dismissSavingsPlanTeaser),
      switchMap(() =>
        this.#depotInfrastructure.dismissSavingsPlanTeaser().pipe(
          map(() => DepotApiActions.dismissSavingsPlanTeaserSuccess()),
          catchError((error: HttpErrorResponse) => of(DepotApiActions.dismissSavingsPlanTeaserFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(DepotApiActions.dismissSavingsPlanTeaserFailure({ error }))),
    );
  });

  public activateDirectDebit$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(DepotActions.activateDirectDebit),
      switchMap(() =>
        this.#depotInfrastructure.activateDirectDebit().pipe(
          map(() => DepotApiActions.activateDirectDebitSuccess()),
          catchError((error: HttpErrorResponse) => of(DepotApiActions.activateDirectDebitFailure({ error }))),
        ),
      ),
      catchError((error: HttpErrorResponse) => of(DepotApiActions.activateDirectDebitFailure({ error }))),
    );
  });

  public handleDepotBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          DepotApiActions.loadUserInterestsFailure,
          DepotApiActions.dismissSavingsPlanTeaserFailure,
          DepotApiActions.activateDirectDebitFailure,
        ),
        tap((error) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
